// NOTE: price display in product card
'use client'

import { Skeleton } from '@/components/ui/skeleton'
import { TagThumbnail } from '@/components/ui/tag-thumbnail'
import { useCampaignPrice } from '@/hooks/common/use-campaign-price'
import { useIsMobileClient } from '@/hooks/common/use-device-type'
import { useCoolClubPrice } from '@/hooks/use-coolclub-price'
import { cn, formatPrice } from '@/lib/utils'
import Image from 'next/image'
import Link from 'next/link'
import { useProductCardContext } from './context/product-card-context'
import { ProductItemVariant } from './product-item-variant'
import { ProductItemVariantMobile } from './product-item-variant-mobile'

/**
 * Hiển thị giá sản phẩm, ưu tiên giá campaign nếu đang hoạt động.
 * Khi đang loading giá campaign, hiển thị skeleton.
 */
export const ProductItemInfo = () => {
  const { product, trackingClickProduct } = useProductCardContext()
  const isMobile = useIsMobileClient()
  const { productData, isCoolClubSale } = useCoolClubPrice(product)

  // Lấy giá campaign (nếu có), kèm trạng thái loading
  const {
    finalPrice: campaignPrice,
    originalPrice: campaignOriginalPrice,
    discountPercent: campaignDiscountPercent,
    shouldShowDiscount: campaignShouldShowDiscount,
    isLoading: isCampaignLoading,
  } = useCampaignPrice({
    regularPrice: product?.regular_price ?? 0,
    comparePrice: product?.compare_price ?? 0,
    productId: product.id,
  })

  // Xác định giá hiển thị dựa trên campaign hoặc giá gốc
  const getDisplayPrice = () => {
    if (campaignShouldShowDiscount) return campaignPrice
    return productData?.regular_price ?? 0
  }

  const getDisplayComparePrice = () => {
    if (campaignShouldShowDiscount && campaignOriginalPrice !== campaignPrice) {
      return campaignOriginalPrice
    }
    if (
      productData?.compare_price &&
      productData.compare_price !== productData.regular_price
    ) {
      return productData.compare_price
    }
    return undefined
  }

  const getDisplayDiscountPercent = () => {
    if (campaignShouldShowDiscount && campaignDiscountPercent > 0) {
      return campaignDiscountPercent
    }
    return productData?.sale_percent
  }

  const displayPrice = getDisplayPrice()
  const displayComparePrice = getDisplayComparePrice()
  const displayDiscountPercent = getDisplayDiscountPercent()

  const handleClick = () => {
    trackingClickProduct()
  }

  return (
    <figcaption className='flex flex-col gap-1 lg:gap-2'>
      {isMobile ? <ProductItemVariantMobile /> : <ProductItemVariant />}

      <ProductTitleLink
        href={productData?.href}
        title={productData?.title}
        productHref={product.href}
        onClick={handleClick}
      />

      <ProductPriceBlock
        isLoading={isCampaignLoading}
        isCoolClubSale={isCoolClubSale}
        displayPrice={displayPrice}
        displayDiscountPercent={displayDiscountPercent}
        displayComparePrice={displayComparePrice}
      />

      {productData?.coolclub_sale?.active && !isCoolClubSale && (
        <p className='line-clamp-1 text-xs text-neutral-500'>
          Giá coolclub: {formatPrice(productData.coolclub_sale.coolclub_price)}
        </p>
      )}

      {productData?.note && (
        <p className='line-clamp-2 font-sans text-xs italic leading-3.5 text-primary-500 lg:text-sm lg:leading-4.5'>
          {productData.note}
        </p>
      )}

      {/* Mô tả ẩn cho screen reader, hiển thị đúng giá theo campaign nếu có */}
      <span className='sr-only'>
        {`Sản phẩm ${productData?.title} có giá ${
          isCampaignLoading ? '...' : formatPrice(displayPrice)
        }${
          !isCampaignLoading && displayDiscountPercent && displayComparePrice
            ? ` (Giảm ${displayDiscountPercent}% từ ${formatPrice(displayComparePrice)})`
            : ''
        }${productData?.note ? `. ${productData.note}` : ''}`}
      </span>
    </figcaption>
  )
}

/**
 * Component hiển thị tiêu đề sản phẩm với link và tracking.
 */
const ProductTitleLink = ({
  href,
  title,
  productHref,
  onClick,
}: {
  href?: string
  title?: string
  productHref: string
  onClick: () => void
}) => (
  <Link
    href={href || '/'}
    className='w-fit'
    aria-label={`Xem chi tiết sản phẩm ${title}`}
    data-tracking-level-99={`Product-item-${productHref.split('/').pop()}`}
    data-ga-tracking-label={productHref.split('/').pop()}
    onClick={onClick}
    prefetch={false}
  >
    <h3 className='line-clamp-2 font-sans text-xs leading-3.5 text-neutral-800 hover:text-primary lg:text-base lg:leading-5'>
      {title}
    </h3>
  </Link>
)

/**
 * Block hiển thị giá, phần trăm giảm, giá gạch ngang, kèm skeleton khi loading.
 */
const ProductPriceBlock = ({
  isLoading,
  isCoolClubSale,
  displayPrice,
  displayDiscountPercent,
  displayComparePrice,
}: {
  isLoading: boolean
  isCoolClubSale: boolean
  displayPrice: number
  displayDiscountPercent?: number
  displayComparePrice?: number
}) => (
  <div className='flex flex-wrap items-center gap-1'>
    {isCoolClubSale && (
      <Image
        src='/icons/icon-price-signin.png'
        alt='Biểu tượng giá ưu đãi khi đăng nhập'
        width={16}
        height={16}
        loading='lazy'
        decoding='async'
      />
    )}
    {isLoading ? (
      <>
        <Skeleton className='h-4 w-16 rounded bg-neutral-200' />
        <Skeleton className='h-4 w-10 rounded bg-neutral-200' />
      </>
    ) : (
      <>
        <p
          className={cn(
            'font-sans text-xs font-bold leading-3.5 text-neutral-900 lg:text-base lg:leading-5',
            isCoolClubSale && 'text-primary'
          )}
        >
          {formatPrice(displayPrice)}
        </p>
        {displayDiscountPercent ? (
          <TagThumbnail
            title={`-${displayDiscountPercent}%`}
            variant='primary'
            size='small'
            className='px-1.5 text-xxs font-semibold leading-3 text-light lg:text-sm lg:leading-4.5'
          />
        ) : null}
        {displayComparePrice ? (
          <p className='text-xs leading-3.5 text-neutral-400 line-through lg:text-sm lg:leading-4.5'>
            {formatPrice(displayComparePrice)}
          </p>
        ) : null}
      </>
    )}
  </div>
)
